import { appConfig } from '../app-config';
import { BaseApi } from '@core/api/base.api';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class SimpleApi extends BaseApi {
  async getList<T>(endpoint: string, params?: {}, abortSignal?: AbortSignal) {
    let url = `${appConfig.apiRoot}${endpoint}`;
    if (params) {
      url = `${url}?${new URLSearchParams(params)}`;
    }
    const options = await this.getOptions(abortSignal);
    return await this.#fetchList<T>(url, options);
  }

  async getRaw<T>(endpoint: string, params?: {}, abortSignal?: AbortSignal) {
    let url = `${appConfig.apiRoot}${endpoint}`;
    if (params) {
      url = `${url}?${new URLSearchParams(params)}`;
    }
    const options = await this.getOptions(abortSignal);
    return await this.#fetchRaw<T>(url, options);
  }

  async getSingle<T>(endpoint: string, params?: {}, abortSignal?: AbortSignal) {
    let url = `${appConfig.apiRoot}${endpoint}`;
    if (params) {
      url = `${url}?${new URLSearchParams(params)}`;
    }
    const options = await this.getOptions(abortSignal);
    return await this.#fetchSingle<T>(url, options);
  }

  async getBlob(endpoint: string, params?: {}, abortSignal?: AbortSignal) {
    let url = `${appConfig.apiRoot}${endpoint}`;
    if (params) {
      url = `${url}?${new URLSearchParams(params)}`;
    }
    const options = await this.getOptions(abortSignal);
    return await this.#fetchBlob(url, options);
  }

  async getById<T>(endpoint: string, id: number, abortSignal?: AbortSignal) {
    const url = `${appConfig.apiRoot}${endpoint}/${id}`;
    const options = await this.getOptions(abortSignal);
    return await this.#fetchSingle<T>(url, options);
  }

  async create<T>(endpoint: string, value: T) {
    const url = `${appConfig.apiRoot}${endpoint}`;
    const options = await this.getOptions();
    options.method = 'POST';
    options.body = JSON.stringify(value);
    return await this.#fetchSingle<T>(url, options);
  }

  async createMultipart<T>(endpoint: string, value: FormData) {
    const url = `${appConfig.apiRoot}${endpoint}`;
    const options = await this.getOptions();
    (options.headers as Headers).delete('content-type'); //Allows the browser to set the correct multipart boundary
    options.method = 'POST';
    options.body = value;
    return await this.#fetchSingle<T>(url, options);
  }

  async update<T>(endpoint: string, id: number, value: T) {
    const url = `${appConfig.apiRoot}${endpoint}/${id}`;
    const options = await this.getOptions();
    options.method = 'PUT';
    options.body = JSON.stringify(value);
    return this.#fetchSingle<T>(url, options);
  }

  async updateMultipart<T>(endpoint: string, id: number, value: FormData) {
    const url = `${appConfig.apiRoot}${endpoint}/${id}`;
    const options = await this.getOptions();
    (options.headers as Headers).delete('content-type'); //Allows the browser to set the correct multipart boundary
    options.method = 'PUT';
    options.body = value;
    return await this.#fetchSingle<T>(url, options);
  }

  async remove<T>(endpoint: string, id: number | string) {
    const url = `${appConfig.apiRoot}${endpoint}/${id}`;
    const options = await this.getOptions();
    options.method = 'DELETE';
    return await this.#fetchSingle<T>(url, options);
  }

  async #fetchSingle<T>(url: string, options: RequestInit) {
    let response: Response;
    try {
      response = await fetch(url, options);
    } catch (error) {
      console.error(error);
      throw error;
    }

    if (!response.ok) {
      throw new Error('Unable to load from API: Status=' + response.status);
    }
    return (await response.json()) as T;
  }

  async #fetchRaw<T>(url: string, options: RequestInit) {
    let response: Response;
    try {
      response = await fetch(url, options);
    } catch (error) {
      console.error(error);
      throw error;
    }

    if (!response.ok) {
      throw new Error('Unable to load from API: Status=' + response.status);
    }
    return (await response.text()) as T;
  }

  async #fetchBlob(url: string, options: RequestInit) {
    try {
      const response = await fetch(url, options);
      return response.blob();
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async #fetchList<T>(url: string, options: RequestInit) {
    let response: Response;
    try {
      response = await fetch(url, options);
    } catch (error) {
      console.error(error);
      throw error;
    }
    if (!response.ok) {
      throw new Error('Unable to load from API: Status=' + response.status);
    }
    return (await response.json()) as T[];
  }
}
